import AndieGenericToolRender from './AndieGenericToolRender';
import { useTranslation } from 'react-i18next';
import {
    CardContent,
    Stack,
    Skeleton,
    useTheme,
    Divider,
    CardActions,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Typography,
    IconButton,
    useMediaQuery,
} from '@mui/material';
import Markdown from 'react-markdown';
import _ from 'lodash';
import { ActionResponseStatusAi } from '@/ai/enum/action-response-status-ai';
import { ActionWorkflowAi } from '@/ai/types/action-workflow-ai';
import { ActionRenderAi } from '@/ai/types/action-render-ai';
import { PostType } from '@/graphql/generated/types.generated';
import { CloseRounded, TipsAndUpdatesRounded } from '@mui/icons-material';
import { useState } from 'react';

type ActionResponse = ActionWorkflowAi<
    {
        type: PostType;
        briefing: string;
        format: number;
        dateTime: Date;
        topic: string;
        briefingVariableUuid?: string | undefined;
        postTypeString?: string | undefined;
    }[]
>;

export type AndieGeneratePostBriefingWorkflowProps = ActionRenderAi<ActionResponse>;

const AndieGeneratePostBriefingWorkflow = (props: AndieGeneratePostBriefingWorkflowProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [previewModal, setPreviewModal] = useState(false);
    // `down('xs')` is `@media (max-width:-0.05px)` in MUI v5+, i.e. it never matches, so the
    // dialog never actually went fullScreen on a phone. `sm` is the phone breakpoint.
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    return (
        <AndieGenericToolRender<ActionResponse>
            title={() => t('andie.tool.generate-post-briefing.title')}
            icon={<TipsAndUpdatesRounded fontSize="small" />}
            loadingMessages={() => [t('andie.tool.generate-post-briefing.loading')]}
            completeMessage={() => t('andie.tool.generate-post-briefing.complete')}
            additionalComponent={(props) => {
                const isComplete = props.status === ActionResponseStatusAi.COMPLETE;
                const hasResult = isComplete && props.result?.result && props.result.result.length > 0;
                const skeletonRows = 22;
                if (isComplete && !hasResult) return null;
                return (
                    <>
                        <CardContent
                            sx={{
                                maxHeight: '45vh',
                                overflow: 'hidden',
                                position: 'relative',
                                '&:after': {
                                    content: '""',
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    height: hasResult ? '150px' : '0px',
                                    width: '100%',
                                    pointerEvents: 'none',
                                    background: 'linear-gradient(to top, white, transparent)',
                                },
                            }}
                        >
                            {hasResult ? (
                                props.result!.result.map((briefing, index: number) => (
                                    <Markdown key={index}>{briefing.briefing || ''}</Markdown>
                                ))
                            ) : (
                                <Stack gap={1}>
                                    {_.times(skeletonRows, (i) => (
                                        <Skeleton
                                            key={i}
                                            variant="rounded"
                                            height={theme.typography.body1.fontSize}
                                            width={
                                                (i + 1) % 5 === 0 || i + 1 === skeletonRows ? '70%' : '100%'
                                            }
                                        />
                                    ))}
                                </Stack>
                            )}
                        </CardContent>
                        {hasResult && (
                            <>
                                <Divider />
                                <CardActions sx={{ justifyContent: 'center' }}>
                                    <Button variant="text" size="small" onClick={() => setPreviewModal(true)}>
                                        {t('str.action.see-more')}
                                    </Button>
                                </CardActions>
                                <Dialog
                                    open={previewModal}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        '& .MuiDialog-container': {
                                            width: '100%',
                                        },
                                    }}
                                    onClose={() => setPreviewModal(false)}
                                    fullScreen={fullScreen}
                                >
                                    <DialogTitle>
                                        <Stack
                                            direction="row"
                                            justifyContent="space-between"
                                            alignItems="center"
                                        >
                                            <Typography variant="h4">
                                                {t('andie.tool.post.briefing')}
                                            </Typography>
                                            <IconButton onClick={() => setPreviewModal(false)}>
                                                <CloseRounded />
                                            </IconButton>
                                        </Stack>
                                    </DialogTitle>
                                    <DialogContent dividers>
                                        {props.result?.result.map((briefing, index: number) => (
                                            <Markdown key={index}>{briefing.briefing || ''}</Markdown>
                                        ))}
                                    </DialogContent>
                                </Dialog>
                            </>
                        )}
                    </>
                );
            }}
            {...props}
        />
    );
};

export default AndieGeneratePostBriefingWorkflow;
